import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Wallet,
  DollarSign,
  Store,
  CheckCircle,
  Clock,
  AlertTriangle,
  Search,
  Download,
  Send,
  CreditCard,
  Receipt,
  Calendar,
  TrendingUp,
  ArrowRight,
  FileText
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayouts() {
  const { token } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all approved sellers
      const sellersRes = await axios.get(`${API}/api/sellers/admin/all?status=approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSellers(sellersRes.data);

      // Fetch all payouts
      const payoutsRes = await axios.get(`${API}/api/sellers/admin/payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayouts(payoutsRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const openPayoutDialog = (seller) => {
    setSelectedSeller(seller);
    const pendingAmount = (seller.total_revenue || 0) - (seller.total_paid || 0);
    setPayoutAmount(pendingAmount.toString());
    setPayoutDialogOpen(true);
  };

  const createPayout = async () => {
    if (!selectedSeller || !payoutAmount) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/api/sellers/admin/payouts`, {
        seller_id: selectedSeller.seller_id,
        amount: parseFloat(payoutAmount),
        method: selectedSeller.payment_method || "invoice"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Utbetaling på ${parseFloat(payoutAmount).toLocaleString()} NOK registrert for ${selectedSeller.company_name}`);
      setPayoutDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Kunne ikke registrere utbetaling");
    } finally {
      setProcessing(false);
    }
  };

  // Calculate totals
  const totalPending = sellers.reduce((sum, s) => {
    const pending = (s.total_revenue || 0) - (s.total_paid || 0);
    return sum + Math.max(0, pending);
  }, 0);
  
  const totalPaidThisMonth = payouts
    .filter(p => {
      const payoutDate = new Date(p.created_at);
      const now = new Date();
      return payoutDate.getMonth() === now.getMonth() && payoutDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const sellersNeedingPayout = sellers.filter(s => {
    const pending = (s.total_revenue || 0) - (s.total_paid || 0);
    return pending > 0 && s.payment_method === "invoice";
  });

  const filteredSellers = sellers.filter(s => 
    s.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.seller_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Utbetalinger">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Utbetalinger">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">
                    {totalPending.toLocaleString()}
                  </p>
                  <p className="text-[#94A3B8] text-sm">NOK ventende utbetaling</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#23F7DD]/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">
                    {totalPaidThisMonth.toLocaleString()}
                  </p>
                  <p className="text-[#94A3B8] text-sm">NOK utbetalt denne måneden</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F7931A]/20 rounded-xl flex items-center justify-center">
                  <Store className="w-6 h-6 text-[#F7931A]" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{sellersNeedingPayout.length}</p>
                  <p className="text-[#94A3B8] text-sm">Selgere venter utbetaling</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{payouts.length}</p>
                  <p className="text-[#94A3B8] text-sm">Totalt utbetalinger</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert for pending payouts */}
        {sellersNeedingPayout.length > 0 && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-yellow-300 font-medium">
                  {sellersNeedingPayout.length} selger{sellersNeedingPayout.length > 1 ? 'e' : ''} venter på utbetaling (faktura-metode)
                </p>
                <p className="text-[#94A3B8] text-sm">
                  Total: {sellersNeedingPayout.reduce((sum, s) => sum + ((s.total_revenue || 0) - (s.total_paid || 0)), 0).toLocaleString()} NOK
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sellers List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-400" />
                Selger Utbetalinger
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Søk selgere..."
                  className="pl-9 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSellers.length === 0 ? (
              <div className="text-center py-12 text-[#94A3B8]">
                <Store className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Ingen selgere funnet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-[#94A3B8] font-medium text-sm">Selger</th>
                      <th className="text-right py-3 px-4 text-[#94A3B8] font-medium text-sm">Total Omsetning</th>
                      <th className="text-right py-3 px-4 text-[#94A3B8] font-medium text-sm">Utbetalt</th>
                      <th className="text-right py-3 px-4 text-[#94A3B8] font-medium text-sm">Ventende</th>
                      <th className="text-center py-3 px-4 text-[#94A3B8] font-medium text-sm">Metode</th>
                      <th className="text-right py-3 px-4 text-[#94A3B8] font-medium text-sm">Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSellers.map(seller => {
                      const pending = Math.max(0, (seller.total_revenue || 0) - (seller.total_paid || 0));
                      return (
                        <tr key={seller.seller_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              {seller.logo_url ? (
                                <img src={seller.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                                  <Store className="w-5 h-5 text-[#F7931A]" />
                                </div>
                              )}
                              <div>
                                <p className="text-white font-medium">{seller.company_name}</p>
                                <p className="text-[#94A3B8] text-xs font-mono">{seller.seller_code}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="text-white font-medium">{(seller.total_revenue || 0).toLocaleString()} NOK</p>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="text-[#23F7DD]">{(seller.total_paid || 0).toLocaleString()} NOK</p>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className={`font-bold ${pending > 0 ? 'text-green-400' : 'text-[#94A3B8]'}`}>
                              {pending.toLocaleString()} NOK
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Badge className={
                              seller.payment_method === 'stripe' 
                                ? 'bg-[#635BFF]/20 text-[#635BFF]' 
                                : 'bg-[#F7931A]/20 text-[#F7931A]'
                            }>
                              {seller.payment_method === 'stripe' ? (
                                <><CreditCard className="w-3 h-3 mr-1" /> Stripe</>
                              ) : (
                                <><Receipt className="w-3 h-3 mr-1" /> Faktura</>
                              )}
                            </Badge>
                          </td>
                          <td className="py-4 px-4 text-right">
                            {pending > 0 && seller.payment_method !== 'stripe' && (
                              <Button
                                size="sm"
                                onClick={() => openPayoutDialog(seller)}
                                className="bg-green-500 text-white hover:bg-green-600"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Utbetal
                              </Button>
                            )}
                            {seller.payment_method === 'stripe' && seller.stripe_onboarded && (
                              <Badge className="bg-green-500/20 text-green-400">
                                <CheckCircle className="w-3 h-3 mr-1" /> Auto
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payouts */}
        {payouts.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#94A3B8]" />
                Siste Utbetalinger
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payouts.slice(0, 10).map(payout => (
                  <div key={payout.payout_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        payout.status === 'completed' ? 'bg-green-400' : 
                        payout.status === 'pending' ? 'bg-yellow-400' : 'bg-red-400'
                      }`} />
                      <div>
                        <p className="text-white font-medium">{payout.seller_name}</p>
                        <p className="text-[#94A3B8] text-xs">
                          {new Date(payout.created_at).toLocaleDateString('no-NO')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{payout.amount?.toLocaleString()} NOK</p>
                      <Badge className={
                        payout.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        payout.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }>
                        {payout.status === 'completed' ? 'Fullført' : 
                         payout.status === 'pending' ? 'Venter' : 'Feilet'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payout Dialog */}
      <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-400" />
              Registrer Utbetaling
            </DialogTitle>
          </DialogHeader>
          
          {selectedSeller && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                {selectedSeller.logo_url ? (
                  <img src={selectedSeller.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                    <Store className="w-6 h-6 text-[#F7931A]" />
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">{selectedSeller.company_name}</p>
                  <p className="text-[#94A3B8] text-sm font-mono">{selectedSeller.seller_code}</p>
                </div>
              </div>

              {selectedSeller.bank_account?.account_number && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-[#94A3B8] text-sm mb-1">Bankkonto</p>
                  <p className="text-white font-mono">{selectedSeller.bank_account.account_number}</p>
                  {selectedSeller.bank_account.bank_name && (
                    <p className="text-[#94A3B8] text-sm">{selectedSeller.bank_account.bank_name}</p>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block">Beløp (NOK)</label>
                <Input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="bg-black/50 border-white/10 text-white text-xl font-bold"
                />
              </div>

              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 text-sm">
                  Når du registrerer denne utbetalingen, vil selgerens "utbetalt" beløp oppdateres automatisk.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setPayoutDialogOpen(false)}
              className="border-white/20 text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={createPayout}
              disabled={processing || !payoutAmount}
              className="bg-green-500 text-white hover:bg-green-600"
            >
              {processing ? "Registrerer..." : "Registrer Utbetaling"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
